import React, { useEffect, useState, lazy } from 'react';
import { ArrowRightIcon, ChevronRightIcon, InfoIcon, SendIcon, TrendingUpIcon, HomeIcon } from 'lucide-react';
import { HeroButton } from './HeroButton';
import { MetricCard } from './MetricCard';
import { BrandBanner } from './BrandBanner';
interface HeroProps {
  /**
   * Optional data-id for testing purposes
   */
  'data-id'?: string;
  /**
   * Video URL for the hero section - can be updated later
   */
  videoUrl?: string;
}
export function Hero({
  'data-id': dataId,
  videoUrl = 'REPLACE_WITH_YOUR_VIDEO_URL' // Placeholder - replace with actual video URL
}: HeroProps) {
  return <div className="relative flex flex-col items-center justify-start w-full max-w-[1280px] mx-auto pt-12 md:pt-16 lg:pt-20 pb-12 md:pb-16 bg-gradient-to-b from-[#121212] via-[#0f0f0f] to-[#121212] text-white font-sans overflow-visible px-6 md:px-8" data-id="element-1504">
      {/* Floating Particles Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1505">
        {[...Array(20)].map((_, i) => <div key={i} className="absolute w-1 h-1 bg-[#3ab795] rounded-full opacity-20" style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animation: `float-particle ${15 + Math.random() * 15}s ease-in-out infinite`,
        animationDelay: `${Math.random() * 5}s`
      }} data-id="element-1506" />)}
      </div>

      {/* Hero Content */}
      <div className="relative flex flex-col items-center justify-center w-full max-w-[1050px] overflow-visible mb-12 md:mb-16 lg:mb-20 z-10" data-id="element-1507">
        {/* Text Content */}
        <div className="flex flex-col items-center justify-center w-full relative overflow-visible" data-id="element-1508">
          {/* Launching Soon Badge */}
          <div className="mb-6 md:mb-8" data-id="element-1509">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-[#3ab795] rounded-full shadow-lg animate-pulse-subtle" data-id="element-1510">
              <span className="text-sm md:text-base font-bold text-white uppercase tracking-wide" data-id="element-1511">
                Launching Soon
              </span>
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" data-id="element-1512" />
            </div>
          </div>

          {/* Heading with Glow Effect */}
          <div className="relative flex flex-col items-center justify-center w-full transform-none mb-6 md:mb-8" data-id="element-1513">
            <h1 className="relative text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight text-center px-4" data-id="element-1514">
              <span className="block mb-2" data-id="element-1515">
                <span className="text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.3)]" data-id="element-1516">
                  Send 1,000+ MLS Offers A Day On{' '}
                </span>
                <span className="relative inline-block" data-id="element-1517">
                  <span className="text-[#3ab795] drop-shadow-[0_0_40px_rgba(58,183,149,0.8)]" data-id="element-1518">
                    Complete Autopilot
                  </span>
                  <span className="absolute -bottom-1 md:-bottom-2 left-0 right-0 h-0.5 md:h-1 bg-[#3ab795]/30 rounded-full" data-id="element-1519"></span>
                  {/* Additional glow layers */}
                  <span className="absolute inset-0 blur-2xl bg-[#3ab795] opacity-20 -z-10" data-id="element-1520"></span>
                </span>
              </span>
            </h1>
          </div>

          {/* Subheading */}
          <div className="relative flex flex-col items-center justify-center w-full max-w-[660px] transform-none px-4" data-id="element-1521">
            <p className="font-normal text-[#b8bcc8] text-center text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed font-light m-0 p-0" data-id="element-1522">
              <span className="font-bold text-white" data-id="element-1523">AcquireFlow</span> helps
              real estate investors automate their MLS offer process,{' '}
              <span className="font-semibold text-[#3ab795]" data-id="element-1524">
                tripling deal volume
              </span>{' '}
              without additional work.
            </p>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center w-full relative overflow-visible gap-3 md:gap-4 mt-8 md:mt-10 px-4" data-id="element-1525">
          {/* Join Beta Button */}
          <div className="w-full sm:w-auto flex-shrink-0" data-id="element-1526">
            <HeroButton href="/trial" text="Join Beta" primary={true} icon={<ArrowRightIcon size={20} data-id="element-1528" />} secondaryIcon={<ChevronRightIcon size={20} data-id="element-1529" />} data-id="element-1527" />
          </div>
          {/* Learn More Button - Updated to link to How It Works */}
          <div className="w-full sm:w-auto flex-shrink-0" data-id="element-1530">
            <HeroButton href="#how-it-works" text="Learn more" primary={false} icon={<InfoIcon size={20} data-id="element-1532" />} secondaryIcon={<ChevronRightIcon size={20} data-id="element-1533" />} data-id="element-1531" />
          </div>
        </div>
      </div>

      {/* Video Hero Section with Floating Cards */}
      <div className="z-7 flex flex-row items-center justify-center w-full max-w-[1034px] min-h-[200px] sm:min-h-[300px] md:min-h-[400px] lg:min-h-[486px] relative overflow-visible p-0 gap-0 mx-auto mt-0 mb-8 md:mb-12" data-id="element-1534">
        {/* Floating Metric Cards - Hidden on mobile, visible on lg+ */}
        {/* Left Card */}
        <div className="hidden lg:block z-20 absolute top-[-110px] left-[-120px] scale-[0.45] transition-all duration-500 hover:scale-[0.48] hover:z-30 cursor-pointer animate-float-card-left origin-center" role="presentation" data-id="element-1535">
          <MetricCard title="Offers Sent Today" value="312" subtitle="sent this morning" progress={85} progressLabel="Daily Target" trend="+47" trendUp={true} variant="default" icon={<SendIcon size={16} strokeWidth={2} data-id="element-1537" />} rotate={-2} data-id="element-1536" />
        </div>

        {/* Center Card */}
        <div className="hidden lg:block z-20 absolute top-[-125px] left-[50%] -translate-x-1/2 scale-[0.45] transition-all duration-500 hover:scale-[0.48] hover:z-30 cursor-pointer animate-float-card-center origin-center" role="presentation" data-id="element-1538">
          <MetricCard title="Agent Opens" value="18.7%" subtitle="opens this week" progress={78} progressLabel="Weekly Goal" trend="+2.4%" trendUp={true} variant="default" icon={<TrendingUpIcon size={16} strokeWidth={2} data-id="element-1540" />} rotate={0} data-id="element-1539" />
        </div>

        {/* Right Card */}
        <div className="hidden lg:block z-20 absolute top-[-117px] right-[-120px] scale-[0.45] transition-all duration-500 hover:scale-[0.48] hover:z-30 cursor-pointer animate-float-card-right origin-center" role="presentation" data-id="element-1541">
          <MetricCard title="Properties Under Contract" value="8" subtitle="active contracts" progress={67} progressLabel="Monthly Target" trend="+2" trendUp={true} variant="primary" icon={<HomeIcon size={16} strokeWidth={2} data-id="element-1543" />} rotate={1.5} data-id="element-1542" />
        </div>

        {/* Main Demo Hero - Supademo Filling Entire Border */}
        <div className="w-full max-w-[1034px] relative rounded-xl md:rounded-2xl shadow-[0_0_40px_15px_rgba(255,255,255,0.25),0_0_20px_5px_rgba(255,255,255,0.15),0px_4px_16px_0px_rgba(0,0,0,0.2)] md:shadow-[0_0_80px_30px_rgba(255,255,255,0.35),0_0_40px_10px_rgba(255,255,255,0.2),0px_8px_24px_0px_rgba(0,0,0,0.3)] transition-all duration-500 hover:shadow-[0_0_80px_30px_rgba(255,255,255,0.35),0_0_40px_10px_rgba(255,255,255,0.2),0px_12px_32px_0px_rgba(0,0,0,0.4)] hover:scale-[1.02] z-10 overflow-hidden" data-id="element-1544">
          <div style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1.946360153256705',
          padding: 0
        }} data-id="element-1545">
            <iframe src="https://app.supademo.com/embed/cmig3afk2cst5b7b4iutf26lr?embed_v=2&utm_source=embed" loading="lazy" title="Send Bulk Offers to Properties on AcquireFlow" allow="clipboard-write" frameBorder="0" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: 'inherit'
          }} data-id="element-1546" />
          </div>
        </div>
      </div>

      {/* Brand Banner */}
      <div className="w-full mt-8 md:mt-12" data-id="element-1547">
        <BrandBanner data-id="element-1548" />
      </div>
    </div>;
}